import React, { useState, useEffect, useRef, FC, useMemo } from 'react';
import { Modal, Slide, Box, Button } from '@mui/material';
import FlowInfo from 'new/components/integrate/FlowSelectionPage/FlowInfo';
import { flowDataMap, flowDataMapItems } from 'new/elements/flows/dataMap';
import { useIsFlowTypeEnabled } from 'new/hooks/useFlowTypeVisibility';
import { FLOW_CATEGORY } from 'new/components/flows/types/flowTypes';
import useAppDispatch from 'js/hooks/useAppDispatch';
import { useSelector } from 'react-redux';
import { getCurrentProjectId } from 'js/reducers/app/selectors';
import { useHistory } from 'react-router-dom';
import { navigateToFlowCreation } from 'new/utils/flowNavigation';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import { CREATE_API_SERVER_FLOW_REQUEST } from 'js/constants/actionTypes';

export type QuickCreateWidgetInfoModalProps = {
  open: boolean;
  flowType: string;
  onClose: () => void;
};

export const QuickCreateWidgetInfoModal: FC<QuickCreateWidgetInfoModalProps> = ({
  open: modalOpen,
  flowType,
  onClose,
}) => {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const currentProjectId = useSelector(getCurrentProjectId);
  const [slideIn, setSlideIn] = useState(false);
  const isNavigating = useRef(false);
  const isCreatingApiServerFlow = useIsLoading(CREATE_API_SERVER_FLOW_REQUEST);

  const data = flowDataMap[flowType];
  const isCurrentFlowTypeEnabled = useIsFlowTypeEnabled(flowType as any);
  const inDevelopment = flowDataMapItems[FLOW_CATEGORY.coming_soon]?.includes(flowType) || false;

  // Configuration for button based on flowType
  const flowTypeCTAConfig = useMemo(() => {
    if (inDevelopment) {
      return null;
    }

    const isEnabled = isCurrentFlowTypeEnabled;

    return {
      label: isEnabled ? 'Create' : 'Request Access',
      variant: isEnabled ? ('ghost' as const) : ('outlined' as const),
      color: isEnabled ? ('primary' as const) : ('basic' as const),
      analyticsEvent: `flow_${isEnabled ? 'create' : 'request_access'}_${flowType}`,
    };
  }, [flowType, isCurrentFlowTypeEnabled, inDevelopment]);

  // When modal opens, trigger slide animation after it mounts
  useEffect(() => {
    if (modalOpen) {
      // Use requestAnimationFrame to ensure DOM is ready
      const timer = requestAnimationFrame(() => {
        setSlideIn(true);
      });
      return () => cancelAnimationFrame(timer);
    } else {
      // Reset slideIn when modal closes
      setSlideIn(false);
    }
  }, [modalOpen]);

  const handleClose = () => {
    setSlideIn(false);
    // onClose will be called after Slide transition completes
  };

  const handleFlowClick = async () => {
    if (isNavigating.current) return;
    isNavigating.current = true;
    try {
      await navigateToFlowCreation({
        history,
        dispatch,
        selectedFlowType: flowType,
        currentProjectId,
      });
      onClose();
    } finally {
      isNavigating.current = false;
    }
  };

  const flowTypeCTA = flowTypeCTAConfig ? (
    <Button
      data-testid="new-flow-proceed"
      onClick={handleFlowClick}
      variant={flowTypeCTAConfig.variant}
      color={flowTypeCTAConfig.color}
      data-analytics={flowTypeCTAConfig.analyticsEvent}
      style={isCreatingApiServerFlow ? { pointerEvents: 'none' } : undefined}
    >
      {flowTypeCTAConfig.label}
    </Button>
  ) : null;

  if (!data) {
    return null;
  }

  return (
    <Modal
      open={modalOpen}
      onClose={handleClose}
      closeAfterTransition
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Slide direction="up" in={slideIn} mountOnEnter unmountOnExit onExited={onClose}>
        <Box
          sx={{
            position: 'relative',
            width: '550px',
            maxWidth: '90vw',
            bgcolor: 'background.paper',
            borderRadius: '8px',
            boxShadow: 24,
            outline: 'none',
          }}
        >
          <FlowInfo flowType={flowType} flowTypeCTA={flowTypeCTA} Image={data.Image} />
        </Box>
      </Slide>
    </Modal>
  );
};
